import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ResponsiveContainer } from "@/components/ui/responsive-container";
import { IntakeData } from "../../pages/Index";

interface DynamicQuestionTreeProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  onBack: () => void;
}

// Question tree configuration based on dental procedures
const QUESTION_TREE = {
  orthodontics: {
    title: "Orthodontic Treatment Questions",
    questions: [
      {
        id: "orthodonticHistory",
        type: "select",
        label: "Have you had orthodontic treatment before?",
        options: [
          "No",
          "Yes - braces",
          "Yes - Invisalign",
          "Yes - retainers",
          "Yes - other",
        ],
      },
      {
        id: "orthodonticGoals",
        type: "multiselect",
        label: "What are your main orthodontic goals?",
        options: [
          "Straighten crooked teeth",
          "Close gaps between teeth",
          "Fix overbite/underbite",
          "Improve bite alignment",
          "Enhance smile aesthetics",
          "Prepare for other dental work",
        ],
      },
      {
        id: "treatmentDuration",
        type: "select",
        label: "What is your preferred treatment timeline?",
        options: [
          "6-12 months",
          "1-2 years",
          "2+ years",
          "Whatever is necessary",
          "As fast as possible",
        ],
      },
      {
        id: "appliancePreference",
        type: "select",
        label: "Do you have a preference for type of braces?",
        options: [
          "Traditional metal braces",
          "Ceramic braces",
          "Lingual braces",
          "Clear aligners",
          "No preference",
        ],
      },
    ],
  },
  implants: {
    title: "Dental Implant Questions",
    questions: [
      {
        id: "missingTeethCount",
        type: "select",
        label: "How many teeth are you missing?",
        options: [
          "1 tooth",
          "2-3 teeth",
          "4-6 teeth",
          "7+ teeth",
          "Full arch (upper)",
          "Full arch (lower)",
          "Both arches",
        ],
      },
      {
        id: "missingTeethDuration",
        type: "select",
        label: "How long have the teeth been missing?",
        options: [
          "Less than 6 months",
          "6 months - 1 year",
          "1-2 years",
          "2-5 years",
          "5+ years",
        ],
      },
      {
        id: "boneGraftHistory",
        type: "select",
        label: "Have you had bone grafting procedures?",
        options: [
          "No",
          "Yes - recent (within 6 months)",
          "Yes - past",
          "Unsure",
          "Recommended but not done",
        ],
      },
      {
        id: "implantExpectations",
        type: "multiselect",
        label: "What are your expectations for dental implants?",
        options: [
          "Restore chewing function",
          "Improve speech",
          "Enhance appearance",
          "Prevent bone loss",
          "Avoid removable dentures",
          "Long-term solution",
        ],
      },
    ],
  },
  cosmetic: {
    title: "Cosmetic Dentistry Questions",
    questions: [
      {
        id: "cosmeticGoals",
        type: "multiselect",
        label: "What cosmetic improvements are you seeking?",
        options: [
          "Whiter teeth",
          "Straighter teeth",
          "Close gaps",
          "Fix chipped teeth",
          "Improve gum line",
          "Replace old fillings",
          "Full smile makeover",
        ],
      },
      {
        id: "previousCosmetic",
        type: "select",
        label: "Have you had cosmetic dental work before?",
        options: [
          "No",
          "Teeth whitening",
          "Veneers",
          "Bonding",
          "Crowns",
          "Multiple procedures",
        ],
      },
      {
        id: "smileConfidence",
        type: "select",
        label: "How confident are you with your current smile?",
        options: [
          "Very confident",
          "Somewhat confident",
          "Not very confident",
          "Not confident at all",
        ],
      },
      {
        id: "socialImpact",
        type: "select",
        label: "Does your smile affect your social or professional life?",
        options: [
          "Not at all",
          "Slightly",
          "Moderately",
          "Significantly",
          "Very significantly",
        ],
      },
    ],
  },
  restoration: {
    title: "Full Mouth Reconstruction Questions",
    questions: [
      {
        id: "reconstructionReason",
        type: "multiselect",
        label: "What issues require full mouth reconstruction?",
        options: [
          "Severe tooth decay",
          "Advanced gum disease",
          "Trauma/injury",
          "Severe wear/grinding",
          "Failed previous dental work",
          "Congenital conditions",
          "TMJ disorders",
        ],
      },
      {
        id: "currentPainLevel",
        type: "select",
        label: "What is your current pain/discomfort level?",
        options: [
          "No pain",
          "Mild discomfort",
          "Moderate pain",
          "Severe pain",
          "Constant pain",
        ],
      },
      {
        id: "functionIssues",
        type: "multiselect",
        label: "What functional problems are you experiencing?",
        options: [
          "Difficulty chewing",
          "Speech problems",
          "Jaw pain/clicking",
          "Teeth sensitivity",
          "Frequent infections",
          "Bad breath",
          "Loose teeth",
        ],
      },
      {
        id: "treatmentPriority",
        type: "select",
        label: "What is your main priority for treatment?",
        options: [
          "Pain relief",
          "Function restoration",
          "Aesthetic improvement",
          "Long-term health",
          "All of the above",
        ],
      },
    ],
  },
  veneers: {
    title: "Veneer Treatment Questions",
    questions: [
      {
        id: "veneerGoals",
        type: "multiselect",
        label: "What improvements are you seeking with veneers?",
        options: [
          "Whiter teeth",
          "Straighter appearance",
          "Close gaps between teeth",
          "Repair chipped teeth",
          "Change tooth shape",
          "Create uniform smile",
          "Cover stains or discoloration",
        ],
      },
      {
        id: "teethToTreat",
        type: "select",
        label: "How many teeth are you considering for veneers?",
        options: [
          "2-4 teeth",
          "6-8 teeth",
          "10-12 teeth",
          "Full smile makeover",
          "Not sure yet",
        ],
      },
      {
        id: "currentTeethCondition",
        type: "select",
        label: "What is the current condition of your teeth?",
        options: [
          "Healthy with minor cosmetic issues",
          "Some chips or cracks",
          "Staining or discoloration",
          "Gaps between teeth",
          "Multiple cosmetic concerns",
        ],
      },
      {
        id: "previousCosmetic",
        type: "select",
        label: "Have you had cosmetic dental work before?",
        options: [
          "No",
          "Teeth whitening",
          "Bonding",
          "Veneers",
          "Crowns",
          "Multiple procedures",
        ],
      },
    ],
  },
  crowns: {
    title: "Crown Treatment Questions",
    questions: [
      {
        id: "crownReason",
        type: "multiselect",
        label: "Why do you need crowns?",
        options: [
          "Protect damaged tooth",
          "Restore broken tooth",
          "Cover large filling",
          "After root canal",
          "Cosmetic improvement",
          "Replace old crown",
          "Strengthen weak tooth",
        ],
      },
      {
        id: "numberOfCrowns",
        type: "select",
        label: "How many crowns do you need?",
        options: [
          "1 crown",
          "2-3 crowns",
          "4-6 crowns",
          "7+ crowns",
          "Not sure",
        ],
      },
      {
        id: "crownMaterial",
        type: "select",
        label: "Do you have a preference for crown material?",
        options: [
          "Porcelain (most natural)",
          "Porcelain-fused-to-metal",
          "All-ceramic",
          "Gold",
          "No preference",
        ],
      },
      {
        id: "urgencyLevel",
        type: "select",
        label: "How urgent is your need for treatment?",
        options: [
          "Emergency - pain or broken tooth",
          "Soon - within 1 month",
          "Planning ahead - 2-3 months",
          "Future planning - flexible timing",
        ],
      },
    ],
  },
  whitening: {
    title: "Teeth Whitening Questions",
    questions: [
      {
        id: "whiteningGoal",
        type: "select",
        label: "What level of whitening are you seeking?",
        options: [
          "Light whitening - 2-3 shades",
          "Moderate whitening - 4-6 shades",
          "Dramatic whitening - 7+ shades",
          "Just want to remove stains",
          "Not sure",
        ],
      },
      {
        id: "stainingType",
        type: "multiselect",
        label: "What type of staining do you have?",
        options: [
          "Coffee/tea stains",
          "Wine stains",
          "Tobacco stains",
          "Age-related yellowing",
          "Medication staining",
          "Fluorosis spots",
          "General discoloration",
        ],
      },
      {
        id: "previousWhitening",
        type: "select",
        label: "Have you tried teeth whitening before?",
        options: [
          "No",
          "Over-the-counter products",
          "Professional whitening",
          "Home whitening trays",
          "Multiple attempts",
        ],
      },
      {
        id: "sensitivityConcern",
        type: "select",
        label: "Do you have sensitive teeth?",
        options: [
          "No sensitivity issues",
          "Mild sensitivity",
          "Moderate sensitivity",
          "Severe sensitivity",
          "Not sure",
        ],
      },
    ],
  },
  other: {
    title: "General Dental Questions",
    questions: [
      {
        id: "lastDentalVisit",
        type: "select",
        label: "When was your last dental visit?",
        options: [
          "Within 6 months",
          "6 months - 1 year",
          "1-2 years",
          "2-5 years",
          "5+ years",
          "Never",
        ],
      },
      {
        id: "dentalConcerns",
        type: "multiselect",
        label: "What dental concerns do you have?",
        options: [
          "Tooth pain",
          "Gum problems",
          "Bad breath",
          "Teeth sensitivity",
          "Cosmetic concerns",
          "Bite problems",
          "Preventive care",
          "No specific concerns",
        ],
      },
      {
        id: "treatmentInterest",
        type: "multiselect",
        label: "What treatments are you interested in learning about?",
        options: [
          "Preventive care",
          "Fillings/restorations",
          "Teeth whitening",
          "Orthodontics",
          "Implants",
          "Cosmetic dentistry",
          "Gum treatment",
          "Not sure yet",
        ],
      },
    ],
  },
};

export default function DynamicQuestionTree({
  data,
  onUpdate,
  onNext,
  onBack,
}: DynamicQuestionTreeProps) {
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});

  const currentQuestions =
    QUESTION_TREE[data.dentalProcedureType as keyof typeof QUESTION_TREE];

  useEffect(() => {
    // Initialize form data with existing values
    if (currentQuestions) {
      const initialData: any = {};
      currentQuestions.questions.forEach((q) => {
        initialData[q.id] =
          (data as any)[q.id] || (q.type === "multiselect" ? [] : "");
      });
      setFormData(initialData);
    }
  }, [data.dentalProcedureType]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleMultiSelectChange = (
    field: string,
    option: string,
    checked: boolean,
  ) => {
    setFormData((prev: any) => {
      const currentValues = prev[field] || [];
      if (checked) {
        return { ...prev, [field]: [...currentValues, option] };
      } else {
        return {
          ...prev,
          [field]: currentValues.filter((item: string) => item !== option),
        };
      }
    });
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (currentQuestions) {
      currentQuestions.questions.forEach((question) => {
        const value = formData[question.id];
        if (question.type === "multiselect") {
          if (!value || value.length === 0) {
            newErrors[question.id] = "Please select at least one option";
          }
        } else {
          if (!value || value.trim() === "") {
            newErrors[question.id] = "This field is required";
          }
        }
      });
    }

    setErrors(newErrors);

    // Scroll to first error if validation fails
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstErrorField = Object.keys(newErrors)[0];
        const element = document.getElementById(firstErrorField);

        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => {
            element.focus();
          }, 500);
        } else {
          // Fallback: scroll to top of form
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onUpdate(formData);
      // Scroll to top before navigating
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        onNext();
      }, 100);
    }
  };

  if (!currentQuestions) {
    // If no specific questions for this procedure type, just continue
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <span className="text-green-600 text-2xl">✓</span>
          </div>
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              All Set!
            </h2>
            <p className="text-gray-600 text-base lg:text-lg">
              No additional questions are needed for {data.dentalProcedureType}.
              You can proceed to the next step.
            </p>
          </div>
          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={onBack}
              className="px-6 h-12 lg:h-14 text-base border-2"
            >
              Back
            </Button>
            <Button
              onClick={onNext}
              className="px-8 h-12 lg:h-14 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              variant="jema"
              size="lg"
            >
              Continue to Photos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderQuestion = (question: any) => {
    const value = formData[question.id];
    const error = errors[question.id];

    switch (question.type) {
      case "select":
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label
              htmlFor={question.id}
              className="text-sm lg:text-base font-medium"
            >
              {question.label}
            </Label>
            <Select
              value={value}
              onValueChange={(val) => handleInputChange(question.id, val)}
            >
              <SelectTrigger
                className={`h-12 lg:h-14 text-base ${error ? "border-red-500" : ""}`}
              >
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {question.options.map((option: string) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );

      case "multiselect":
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label className="text-sm lg:text-base font-medium">
              {question.label}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3 p-3 lg:p-4 border rounded-md">
              {question.options.map((option: string) => (
                <div
                  key={option}
                  className="flex items-center space-x-2 lg:space-x-3 min-h-touch"
                >
                  <Checkbox
                    id={`${question.id}-${option}`}
                    checked={(value || []).includes(option)}
                    onCheckedChange={(checked) =>
                      handleMultiSelectChange(
                        question.id,
                        option,
                        checked as boolean,
                      )
                    }
                    className="w-4 h-4 lg:w-5 lg:h-5"
                  />
                  <Label
                    htmlFor={`${question.id}-${option}`}
                    className="text-sm lg:text-base font-normal cursor-pointer leading-tight"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </div>
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label
              htmlFor={question.id}
              className="text-sm lg:text-base font-medium"
            >
              {question.label}
            </Label>
            <Textarea
              id={question.id}
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              className={`min-h-[80px] lg:min-h-[100px] text-base ${error ? "border-red-500" : ""}`}
              rows={3}
            />
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label
              htmlFor={question.id}
              className="text-sm lg:text-base font-medium"
            >
              {question.label}
            </Label>
            <Input
              id={question.id}
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              className={`h-12 lg:h-14 text-base ${error ? "border-red-500" : ""}`}
            />
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="h-full">
      {/* Mobile: Traditional card layout */}
      <div className="lg:hidden">
        <ResponsiveContainer variant="standard">
          <Card className="w-full">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">
                {currentQuestions.title}
              </CardTitle>
              <p className="text-gray-600 text-sm">
                Please answer these additional questions specific to{" "}
                {data.dentalProcedureType}.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-6">
                {currentQuestions.questions.map(renderQuestion)}
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button
                  onClick={handleSubmit}
                  className="w-full h-12 text-base hover:shadow-lg transition-all duration-200"
                  variant="jema"
                  size="lg"
                >
                  Continue to Photos
                </Button>
                <Button
                  variant="outline"
                  onClick={onBack}
                  className="w-full h-12 text-base"
                >
                  Back
                </Button>
              </div>
            </CardContent>
          </Card>
        </ResponsiveContainer>
      </div>

      {/* Desktop/Tablet: Modern full-screen layout */}
      <div className="hidden lg:block max-w-4xl mx-auto">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900">
              {currentQuestions.title}
            </h2>
            <p className="text-lg text-gray-600">
              Please answer these additional questions specific to{" "}
              {data.dentalProcedureType}.
            </p>
          </div>

          {/* Questions in modern card layout */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 xl:p-10">
            <div className="space-y-8">
              {currentQuestions.questions.map(renderQuestion)}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-6 justify-center pt-8">
            <Button
              variant="outline"
              onClick={onBack}
              className="px-8 h-14 text-lg border-2 hover:border-[#166B93] hover:text-[#166B93] transition-colors"
            >
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              className="px-12 h-14 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              variant="jema"
              size="lg"
            >
              Continue to Photos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
